import { useMemo, useState } from 'react';
import { datasDaSerie } from '@zenda/kernel/agenda';
import type { SlotCheck, Uuid } from '@zenda/api-contract';
import { useSlotCheck } from './queries';

export interface DataDaSerie {
  readonly date: string;
  readonly hora: string;
  /** Entra sozinha, sem encaixe. */
  readonly livre: boolean;
  readonly motivo: SlotCheck['reason'];
}

/**
 * "Repetir N vezes, a cada M semanas" — e o veredito de cada data.
 *
 * ── Por que um hook, e não código em cada tela ──
 * As duas telas de marcação tinham a mesma sequência escrita duas vezes: gerar as
 * datas, perguntar ao servidor, contar conflitos, decidir o que entra com e sem
 * encaixe. E não eram iguais: a da conversa pulava a PRIMEIRA data da conferência e a
 * declarava livre, porque ela veio da busca; a da agenda conferia todas.
 *
 * A suposição da conversa parecia inofensiva e não era. A busca é um retrato com 30 s
 * de validade, e desde que a folga de deslocamento e a antecedência mínima passaram a
 * ter veredito, a primeira data podia voltar `deslocamento` — e a tela dizia "livre"
 * porque nem tinha perguntado. Agora as duas conferem a série inteira.
 *
 * ── Por que o veredito vem do servidor, e não da lista da busca ──
 * A lista é uma janela dos próximos horários, e a 5ª sessão de uma série semanal cai
 * bem depois do fim dela. Procurar lá dizia "não está na lista, logo está ocupado" e
 * pintava de vermelho uma data livre.
 */
export function useSerieDeConsultas(p: {
  readonly clientId: Uuid;
  /** `null` enquanto a atendente não escolheu horário: não há série a propor. */
  readonly inicio: { readonly date: string; readonly hora: string } | null;
  readonly duracao: number;
  readonly locationId: Uuid | null;
  /** Com encaixe, data ocupada deixa de ser pulada e entra por decisão. */
  readonly encaixe: boolean;
}): {
  readonly sessoes: number;
  readonly setSessoes: (n: number) => void;
  readonly cadaSemanas: number;
  readonly setCadaSemanas: (n: number) => void;
  readonly serie: readonly DataDaSerie[];
  readonly conflitos: number;
  /** O que de fato vai ser gravado, dado o encaixe. */
  readonly aMarcar: readonly DataDaSerie[];
  readonly conferindo: boolean;
} {
  const [sessoes, setSessoes] = useState(1);
  const [cadaSemanas, setCadaSemanas] = useState(1);

  const datas = useMemo(
    () =>
      p.inicio === null
        ? []
        : datasDaSerie(p.inicio.date, sessoes, cadaSemanas).map((date) => ({
            date,
            hora: (p.inicio as { hora: string }).hora,
          })),
    [p.inicio, sessoes, cadaSemanas],
  );

  const conferencia = useSlotCheck(p.clientId, p.duracao, p.locationId, datas);

  const serie = useMemo<readonly DataDaSerie[]>(() => {
    const veredito = new Map(
      (conferencia.data?.results ?? []).map((r) => [`${r.date} ${r.hora}`, r]),
    );
    return datas.map((d) => {
      const v = veredito.get(`${d.date} ${d.hora}`);
      return {
        date: d.date,
        hora: d.hora,
        // Enquanto a conferência não volta, nada é acusado de ocupado: acusar antes de
        // saber pinta de vermelho a data que a atendente está lendo em voz alta.
        livre: v === undefined ? true : v.free,
        motivo: v?.reason ?? ('livre' as const),
      };
    });
  }, [datas, conferencia.data]);

  return {
    sessoes,
    setSessoes: (n) => setSessoes(Math.max(1, Math.min(24, n))),
    cadaSemanas,
    setCadaSemanas: (n) => setCadaSemanas(Math.max(1, Math.min(8, n))),
    serie,
    conflitos: serie.filter((x) => !x.livre).length,
    aMarcar: serie.filter((x) => x.livre || p.encaixe),
    conferindo: conferencia.isPending && datas.length > 0,
  };
}
